/**
 * Renders an "upgrade required" panel when a user without the right
 * tier hits a gated route. Sits inside <RequireTier>; the parent
 * decides when to show it.
 *
 * Copy is tier-aware:
 *   • Free → Essentials  ($49.99/mo) for the heatmaps
 *   • Free → Pro         ($99.99/mo) for replay / scanner / etc.
 *   • Essentials → Pro   ($99.99/mo) for the Pro-only stuff
 *
 * No Stripe wiring yet — the "Upgrade" buttons go to /pricing today.
 * When Stripe lands (Day 8 of the launch plan) the CTA URLs swap
 * to per-tier checkout sessions; this component doesn't change.
 *
 * Source of truth for prices: this file matches the user's product
 * spec ($49.99 / $99.99). The Pricing.tsx marketing page mirrors
 * the same numbers — keep them in sync.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import type { Tier } from '../../lib/types';

const PRICE_FOR: Record<Tier, string> = {
  free: '$0',
  essentials: '$49.99/mo',
  pro: '$99.99/mo',
};

const TIER_LABEL: Record<Tier, string> = {
  free: 'Free',
  essentials: 'Essentials',
  pro: 'Pro',
};

interface UpgradeNudgeProps {
  required: Tier;
  currentTier: Tier;
  featureLabel?: string;
}

const UpgradeNudge: React.FC<UpgradeNudgeProps> = ({ required, currentTier, featureLabel }) => {
  const reqLabel = TIER_LABEL[required];
  const reqPrice = PRICE_FOR[required];

  return (
    <div className="gv-options-body" style={{ paddingTop: '3rem' }}>
      <section className="gv-card" style={{
        maxWidth: 540, margin: '0 auto', textAlign: 'center',
        padding: '2.5rem 1.75rem',
      }}>
        <div
          aria-hidden="true"
          style={{
            fontSize: 38, lineHeight: 1, marginBottom: 14,
            color: 'var(--accent)', opacity: 0.6,
          }}
        >
          🔒
        </div>

        <h2 style={{
          fontSize: 22, fontWeight: 700, marginBottom: 10,
          color: 'var(--text)',
        }}>
          {featureLabel ?? 'This view'} is on the {reqLabel} plan
        </h2>

        <p style={{
          color: 'var(--text-2)', fontSize: 14, lineHeight: 1.55,
          maxWidth: 420, margin: '0 auto 1.5rem',
        }}>
          You&apos;re on <strong style={{ color: 'var(--text)' }}>{TIER_LABEL[currentTier]}</strong>.
          {' '}Upgrade to <strong style={{ color: 'var(--accent)' }}>{reqLabel}</strong>
          {' '}({reqPrice}) to unlock {featureLabel ?? 'this view'}.
        </p>

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/pricing" className="gv-cta-primary">
            See plans →
          </Link>
          <Link to="/app/settings" className="gv-cta-secondary">
            Go to settings
          </Link>
        </div>

        <p style={{
          marginTop: '1.5rem', fontSize: '0.72rem', color: 'var(--text-4)',
        }}>
          Public checkout opens at GA — for now we set up paid accounts manually within one business day.
        </p>
      </section>
    </div>
  );
};

export default UpgradeNudge;
